import { useContext, useEffect, useRef, useState } from "react"
import { Box, Chip, Dialog, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material"

import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LayersIcon from '@mui/icons-material/Layers';

import CompanyContext		from "../context"
import useFetch 			from "../../../api/api"
import config 				from "../../../common/Config";

import RiskPanel    from "../../User/UserSettings/components/RiskPanel"
import GrowthPanel  from "../../User/UserSettings/components/GrowthPanel"
import ApprovalPanel from "../../User/UserSettings/components/ApprovalPanel"
import Loading      from "../../../components/Loading"

const PanelUserParameters = () => {

	const { selectedStock }										= useContext(CompanyContext)
	const privateFetch											= useFetch()

	const [riskParameters, setRiskParameters]					= useState()
	const [growthParameters, setGrowthParameters]				= useState()
	const [indicators, setIndicators]							= useState()
	const [fields, setFields]									= useState([])
	const [contextId, setContextId]								= useState(null)
	const [contextName, setContextName]							= useState(null)

	const [isRiskDialogOpen, setIsRiskDialogOpen]				= useState(false)
	const [isGrowthDialogOpen, setIsGrowthDialogOpen]			= useState(false)
	const [isRiskGrowthDialogOpen, setIsRiskGrowthDialogOpen]	= useState(false)

	const [isLoading, setIsLoading]								= useState(true)
	const hasPendingReload										= useRef(false)

	useEffect(() => {
		loadParameters()
	}, [selectedStock])

	const loadParameters = () => {
		setIsLoading(true)

		Promise.all([
			privateFetch(config.apiUrl + '/api/settings/contextForTicker?ticker=' + selectedStock).then(r => r.json()),
			privateFetch(config.apiUrl + '/api/indicators/company/' + selectedStock).then(r => r.json()),
			privateFetch(config.apiUrl + '/api/screener/fields').then(r => r.json()),
		]).then(([ctxResult, indicatorsResults, fieldsResults]) => {
			const resolvedContextId   = ctxResult?.contextId   ?? null
			const resolvedContextName = ctxResult?.contextName ?? null
			const ctxParam = resolvedContextId ? `?contextId=${resolvedContextId}` : ''

			Promise.all([
				privateFetch(config.apiUrl + '/api/settings/riskParameters'      + ctxParam).then(r => r.json()),
				privateFetch(config.apiUrl + '/api/settings/growthParameters'    + ctxParam).then(r => r.json()),
			]).then(([riskResults, growthResults]) => {
				setRiskParameters(riskResults)
				setGrowthParameters(growthResults)
				setIndicators(indicatorsResults.length > 0 ? indicatorsResults[0] : [])
				setFields(fieldsResults)
				setContextId(resolvedContextId)
				setContextName(resolvedContextName)
				setIsLoading(false)
			})
		})
	}

	const handleDialogClose = (pStateFunction) => {
		pStateFunction(false)
		if (hasPendingReload.current) {
			hasPendingReload.current = false
			setIsLoading(true)
			loadParameters()
		}
	}

	if (isLoading) return <Loading/>

	return (
		<Box sx={{pt: 1, paddingX: 2, marginBottom: 5}}>
			<TableContainer component={Paper} sx={{ maxWidth: 600, margin: "auto", mt: 1 }}>
				<Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} marginRight={1} color={'text.secondary'}>
					<Typography variant="h6" sx={{ p: 2 }}>Parâmetros de Risco x Crescimento</Typography>
					<SettingsIcon sx={{cursor: 'pointer'}} onClick={() => setIsRiskGrowthDialogOpen(true)}/>
				</Box>

				<Table>
					<TableHead>
					<TableRow>
						<TableCell align="center">Risco</TableCell>
						<TableCell align="center">Crescimento</TableCell>
						<TableCell align="center">Parecer</TableCell>
					</TableRow>
					</TableHead>

					<TableBody>
						<TableRow>
							<TableCell align="center">{indicators.risk}</TableCell>
							<TableCell align="center">{indicators.growth}</TableCell>
							<TableCell align="center" sx={{color : indicators.growthRisk == 'Aprovado' ? 'green' : 'red'}}>{indicators.growthRisk}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			{contextName && (
				<Box display="flex" justifyContent="center" mt={1.5}>
					<Chip
						icon={<LayersIcon />}
						label={`Contexto: ${contextName}`}
						size="small"
						color="primary"
						variant="outlined"
					/>
				</Box>
			)}

			<TableContainer component={Paper} sx={{ maxWidth: 600, margin: "auto", mt: 2 }}>
				<Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} marginRight={1} color={'text.secondary'}>
					<Typography variant="h6" sx={{ p: 2 }}>Parâmetros de Risco</Typography>
					<SettingsIcon sx={{cursor: 'pointer'}} onClick={() => setIsRiskDialogOpen(true)}/>
				</Box>
				<Table>
					<TableHead>
					<TableRow>
						<TableCell><strong>Campo</strong></TableCell>
						<TableCell align="center">Baixo</TableCell>
						<TableCell align="center">Médio</TableCell>
						<TableCell align="center">Alto</TableCell>
						<TableCell align="center">Valor</TableCell>
					</TableRow>
					</TableHead>

					<TableBody>
					{riskParameters.map((row, i) => {

						var riskLevel = 'Fora dos Parâmetros'

						if (parseOperation(row.lowRiskOperator, row.lowRiskParameter, indicators[row.field.id])) {
							riskLevel = 'Baixo'
						}
						else if (parseOperation(row.medRiskOperator, row.medRiskParameter, indicators[row.field.id])) {
							riskLevel = 'Médio'
						}
						else if (parseOperation(row.higRiskOperator, row.higRiskParameter, indicators[row.field.id])) {
							riskLevel = 'Alto'
						}

						return (
							<TableRow key={i}>
								<TableCell>{row.field.label}</TableCell>
								<TableCell align="center" sx={riskLevel == 'Baixo' ? {color: 'success.main', fontWeight: 'bold' } : null}>{operatorToSymbol(row.lowRiskOperator)} {row.lowRiskParameter}</TableCell>
								<TableCell align="center" sx={riskLevel == 'Médio' ? {color: 'warning.main', fontWeight: 'bold' } : null}>{operatorToSymbol(row.medRiskOperator)} {row.medRiskParameter}</TableCell>
								<TableCell align="center" sx={riskLevel == 'Alto' ? {color: 'error.main', fontWeight: 'bold' } : null}>{operatorToSymbol(row.higRiskOperator)} {row.higRiskParameter}</TableCell>
								<TableCell align="center">{indicators[row.field.id]}</TableCell>
							</TableRow>
						)
					})}
					</TableBody>
				</Table>
			</TableContainer>

			<TableContainer component={Paper} sx={{ maxWidth: 600, margin: "auto", mt: 2 }}>
				<Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} marginRight={1} color={'text.secondary'}>
					<Typography variant="h6" sx={{ p: 2 }}>Parâmetros de Crescimento</Typography>
					<SettingsIcon sx={{cursor: 'pointer'}} onClick={() => setIsGrowthDialogOpen(true)}/>
				</Box>

				<Table>
					<TableHead>
					<TableRow>
						<TableCell><strong>Campo</strong></TableCell>
						<TableCell align="center" sx={indicators.growth === 'Lento' ? { color: 'success.main', fontWeight: 'bold' } : null}>Lento</TableCell>
						<TableCell align="center" sx={indicators.growth === 'Médio' ? { color: 'success.main', fontWeight: 'bold' } : null}>Médio</TableCell>
						<TableCell align="center" sx={indicators.growth === 'Rápido' ? { color: 'success.main', fontWeight: 'bold' } : null}>Rápido</TableCell>
						<TableCell align="center">Valor</TableCell>
					</TableRow>
					</TableHead>

					<TableBody>

					<TableRow>
						<TableCell>{growthParameters.field.label}</TableCell>
						<TableCell align="center" sx={indicators.growth === 'Lento' ? { color: 'success.main', fontWeight: 'bold' } : null}>
							Maior que <br/>
							{growthParameters.lowGrowth}
							{growthParameters.lowGrowthIndicator !== '' ? (
								<Typography sx={{ font: 'inherit', display: 'inline' }}>&nbsp;+&nbsp;
									<Tooltip title={indicatorLabel(growthParameters.higGrowthIndicator)}>
										<u style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}>
											{indicators[growthParameters.lowGrowthIndicator.replace('@','')]}
										</u>
									</Tooltip>
								</Typography>
							) : ''}
						</TableCell>
						<TableCell align="center" sx={indicators.growth === 'Médio' ? { color: 'success.main', fontWeight: 'bold' } : null}>
							Maior que <br/>
							{growthParameters.medGrowth}
							{growthParameters.medGrowthIndicator !== '' ? (
								<Typography sx={{ font: 'inherit', display: 'inline' }}>&nbsp;+&nbsp;
									<Tooltip title={indicatorLabel(growthParameters.higGrowthIndicator)}>
										<u style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}>
											{indicators[growthParameters.medGrowthIndicator.replace('@','')]}
										</u>
									</Tooltip>
								</Typography>
							) : ''}
						</TableCell>
						<TableCell align="center" sx={indicators.growth === 'Rápido' ? { color: 'success.main', fontWeight: 'bold' } : null}>
							Maior que <br/>
							{growthParameters.higGrowth}
							{growthParameters.higGrowthIndicator !== '' ? (
								<Typography sx={{ font: 'inherit', display: 'inline' }}>&nbsp;+&nbsp;
									<Tooltip title={indicatorLabel(growthParameters.higGrowthIndicator)}>
										<u style={{ textDecoration: 'underline dotted', cursor: 'pointer' }}>
											{indicators[growthParameters.higGrowthIndicator.replace('@','')]}
										</u>
									</Tooltip>
								</Typography>
							) : ''}
						</TableCell>
						<TableCell align="center">{indicators[growthParameters.field.id]}</TableCell>
					</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<Dialog onClose={() => handleDialogClose(setIsRiskDialogOpen)} open={isRiskDialogOpen} fullWidth maxWidth="xl">
				<DialogTitle variant="h6" display="flex" alignItems="center">
					<Box flex={1}>Minhas Definições de Risco{contextName ? ` — ${contextName}` : ''}</Box>
					<CloseIcon onClick={() => handleDialogClose(setIsRiskDialogOpen)} sx={{ cursor: 'pointer' }} />
				</DialogTitle>
				<Box sx={{ p: 2 }}>
					<RiskPanel fields={fields} contextId={contextId} onSaved={() => { hasPendingReload.current = true }} />
				</Box>
			</Dialog>

			<Dialog onClose={() => handleDialogClose(setIsGrowthDialogOpen)} open={isGrowthDialogOpen} fullWidth maxWidth="sm">
				<DialogTitle variant="h6" display="flex" alignItems="center">
					<Box flex={1}>Minhas Definições de Crescimento{contextName ? ` — ${contextName}` : ''}</Box>
					<CloseIcon onClick={() => handleDialogClose(setIsGrowthDialogOpen)} sx={{ cursor: 'pointer' }} />
				</DialogTitle>
				<Box sx={{ p: 2 }}>
					<GrowthPanel fields={fields} contextId={contextId} onSaved={() => { hasPendingReload.current = true }} />
				</Box>
			</Dialog>

			<Dialog onClose={() => handleDialogClose(setIsRiskGrowthDialogOpen)} open={isRiskGrowthDialogOpen} fullWidth maxWidth="xs">
				<DialogTitle variant="h6" display="flex" alignItems="center">
					<Box flex={1}>Minhas Definições de Risco x Crescimento{contextName ? ` — ${contextName}` : ''}</Box>
					<CloseIcon onClick={() => handleDialogClose(setIsRiskGrowthDialogOpen)} sx={{ cursor: 'pointer' }} />
				</DialogTitle>
				<Box sx={{ p: 2 }}>
					<ApprovalPanel contextId={contextId} onSaved={() => { hasPendingReload.current = true }} />
				</Box>
			</Dialog>

		</Box>
	)

}

function operatorToSymbol(operator) {
	switch (operator) {
		case 'lt'	: return '<'
		case 'le'	: return '≤';
		case 'eq'	: return '=';
		case 'ne'	: return '≠';
		case 'ge'	: return '≥';
		case 'gt'	: return '>';
		case 'in'	: return '∈';
		case 'ni'	: return '∉';
		case 'ct'	: return '⊃';
		case 'nc'	: return '⊅';
		default		: return ''
	}
}

function parseOperation(operation, parameter, value) {
	switch (operation) {
		case 'lt'	: return parseFloat(value) < parseFloat(parameter)
		case 'le'	: return parseFloat(value) <= parseFloat(parameter)
		case 'eq'	: return parseFloat(value) == parseFloat(parameter)
		case 'ne'	: return parseFloat(value) != parseFloat(parameter)
		case 'ge'	: return parseFloat(value) >= parseFloat(parameter)
		case 'gt'	: return parseFloat(value) > parseFloat(parameter)
		case 'in'	: return parameter.split(',').includes(value)
		case 'ni'	: return !parameter.split(',').includes(value)
		case 'ct'	: return value.includes(parameter)
		case 'nc'	: return !value.includes(parameter)
		default		: return false
	}
}

function indicatorLabel(field) {
	switch (field) {
		case '@pib_cagr_5y'			: return 'CAGR do PIB (5A)'
		case '@pib_cagr_10y' 		: return 'CAGR do PIB (10A)'
		case '@inflation_cagr_5y' 	: return 'CAGR da Inflação (5A)'
		case '@inflation_cagr_10y' 	: return 'CAGR da Inflação (10A)'
	}
}

export default PanelUserParameters
